import { CSSProperties, useEffect, useState } from "react";
import styled from "styled-components";
import media from "styled-media-query";

import { Text, ColorV2, FAQs } from "@adoptaunabuelo/react-components";
import {
	CalendarCheck,
	Check,
	Clock,
	Glasses,
	HeartCrack,
	RockingChair,
	Smartphone,
	Star,
	Users,
} from "lucide-react";
import Google from "../../../assets/logo/Google";
import ActivitiesList from "../../../components/list/ActivitiesList";
import TestimonyList from "../../../components/list/TestimonyList";
import PressList from "../../../components/list/PressList";
import ReliefModal from "../../../components/modal/ReliefModal";
import ProtectedDonation from "../../../components/modal/ProtectedDonation";

const Container = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
`;
const Section = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	padding: 64px 0px;
	${media.lessThan("medium")`
        padding: 48px 0px;
    `}
`;
const SectionDivider = styled(Section)`
	border-bottom: 1px solid ${ColorV2.border.neutralSoft};
`;
const CarouselSection = styled(SectionDivider)`
	${media.lessThan("medium")`
		border-bottom: none;
	`}
`;
const IssuesSectionOuter = styled(Section)`
	padding: 0px;
`;
const IndependenceSection = styled(SectionDivider)`
	${media.lessThan("medium")`
        background: var(--surface-primary-hard, #00315C);
        color: var(--text-invert, #FFF);
        box-sizing: border-box;
        width: 100vw;
        margin-left: calc(50% - 50vw);
        margin-right: calc(50% - 50vw);
        padding: 48px 24px;
    `}
`;
const SectionTitleH2 = styled(Text)`
	margin-bottom: 64px;
	${media.lessThan("medium")`
		margin-bottom: 32px;
	`}
`;
const SectionTitleH2Small = styled(Text)`
	margin-bottom: 24px;
`;
const SectionTitleH2Compact = styled(Text)`
	margin-bottom: 16px;
`;
const DestinationSection = styled.div`
	display: flex;
	flex-direction: column;
	padding: 64px 0;
	border-bottom: 1px solid ${ColorV2.border.neutralSoft};
	${media.lessThan("medium")`
        background: var(--surface-background, #F9F6F3);
        box-sizing: border-box;
        width: 100vw;
        margin-left: calc(50% - 50vw);
        margin-right: calc(50% - 50vw);
        padding: 48px 24px;
        border-bottom: none;
    `}
`;
const ProgressBar = styled.div`
	display: flex;
	height: 12px;
	width: 100%;
	border-radius: 1000px;
	border: 1px solid ${ColorV2.border.neutralSoft};
	padding: 2px;
	overflow: hidden;
	margin-bottom: 24px;
	box-sizing: border-box;
	${media.lessThan("medium")`
        display: none;
    `}
`;
const ProgressBarSegment = styled.div<{ $flex: number; $color: string }>`
	flex: ${(p) => p.$flex};
	background-color: ${(p) => p.$color};
	&:first-child {
		border-top-left-radius: 1000px;
		border-bottom-left-radius: 1000px;
	}
	&:last-child {
		border-top-right-radius: 1000px;
		border-bottom-right-radius: 1000px;
	}
`;
const ChartRow = styled.div`
	display: flex;
	gap: 16px;
	align-items: flex-start;
	${media.lessThan("medium")`
        flex-direction: column;
        gap: 32px;
	`}
`;
const ChartCol = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	${media.lessThan("medium")`
        width: 100%;
	`}
`;
const ChartColPrimary = styled(ChartCol)`
	width: 287px;
`;
const ChartColNeutral = styled(ChartCol)`
	width: 244px;
`;
const ChartColMedium = styled(ChartCol)`
	flex: 1;
	min-width: 0;
`;
const ChartHeader = styled.div`
	display: flex;
	flex-direction: column;
`;
const PercentLarge = styled(Text)`
	font-size: 60px !important;
	line-height: 68px !important;
	font-weight: 600 !important;
	letter-spacing: -0.6px !important;
	color: ${ColorV2.text.primary};
	margin: 0;
`;
const PercentMedium = styled(Text)`
	font-size: 40px !important;
	line-height: 48px !important;
	font-weight: 600 !important;
	letter-spacing: -0.4px !important;
	color: ${ColorV2.text.neutralHard};
	margin: 0;
`;
const PercentSmall = styled(Text)`
	font-size: 24px !important;
	line-height: 32px !important;
	font-weight: 600 !important;
	color: ${ColorV2.text.neutralMedium};
	margin: 0;
`;
const ChartLabel = styled(Text) <{ $color: string }>`
	font-size: 17px !important;
	line-height: 24px !important;
	font-weight: 500 !important;
	color: ${(p) => p.$color};
	margin: 0;
`;
const BulletsList = styled.div`
	display: flex;
	flex-direction: column;
`;
const BulletItem = styled.div`
	display: flex;
	gap: 8px;
	align-items: flex-start;
	padding: 4px 0;
`;
const BulletIconWrap = styled.div`
	display: flex;
	padding-top: 1px;
	flex-shrink: 0;
`;
const BulletItemText = styled(Text)`
	font-size: 14px !important;
	line-height: 22px !important;
	color: ${ColorV2.text.neutralHard};
	margin: 0;
	flex: 1;
	min-width: 0;
`;
const ProtectedDonationWrap = styled.div`
	margin-bottom: 48px;
`;

const Col = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
`;
const IntroRow = styled.div`
	display: flex;
	gap: 32px;
	align-items: flex-end;
	justify-content: space-between;
	width: 100%;
	box-sizing: border-box;

	${media.lessThan("medium")`
		flex-direction: column;
		gap: 24px;
		align-items: flex-start;
	`}
`;
const IntroMain = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	max-width: 439px;

	${media.lessThan("medium")`
		width: 100%;
		max-width: none;
	`}
`;
const IntroTitle = styled(Text)`
	margin-bottom: 16px;
`;
const IntroLead = styled(Text)`
	margin-bottom: 0;
	font-size: 15px;
	line-height: 24px;
`;
const ReliefCard = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	max-width: 240px;
	padding: 12px 24px;
	border-radius: 16px;
	background-color: ${ColorV2.surface.secondarySoft};
	box-sizing: border-box;

	${media.lessThan("medium")`
		width: 100%;
		max-width: none;
		padding: 16px;
		flex-direction: row;
		align-items: center;
		gap: 16px;
	`}
`;
const ReliefIcon = styled.img`
	width: 48px;
	height: 48px;
	margin-bottom: 12px;

	${media.lessThan("medium")`
		width: 48px;
		height: 48px;
		margin-bottom: 0;
		flex-shrink: 0;
	`}
`;
const ReliefText = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;

	${media.greaterThan("medium")`
		font-size: 14px;
		line-height: 20px;
	`}
`;
const ReliefBody = styled(Text)`
	margin-bottom: 0;
	font-size: 14px;
	line-height: 20px;
`;
const ReliefBold = styled.span`
	font-weight: 600;
`;
const EmbajadorImage = styled.img`
	width: 100%;
	aspect-ratio: 200 / 100;
	object-fit: cover;
	border-radius: 16px;
	margin-top: 48px;

	${media.lessThan("medium")`
		margin-top: 24px;
	`}
`;
const IssuesSection = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	border-bottom: 1px solid ${ColorV2.border.neutralSoft};
	border-top: 1px solid ${ColorV2.border.neutralSoft};
	padding-top: 64px;
	padding-bottom: 64px;

	${media.lessThan("medium")`
		background: var(--surface-background, #F9F6F3);
		box-sizing: border-box;
		width: 100vw;
        margin-left: calc(50% - 50vw);
        margin-right: calc(50% - 50vw);
        padding: 48px 24px;
		border-bottom: none;
		border-top: none;
	`}
`;
const IssuesTitle = styled(Text)`
	color: var(--text-clear-neutral-hard, rgba(0, 29, 61, 0.92));
	font-feature-settings: "liga" off, "clig" off;
	font-family: Poppins;
	font-size: 28px !important;
	font-style: normal;
	line-height: 36px !important;
	margin-bottom: 48px;

	${media.lessThan("medium")`
		font-size: 24px !important;
		font-weight: 400 !important;
		line-height: 32px !important;
		letter-spacing: -0.24px;
		margin-bottom: 32px;
	`}
`;
const IssuesLead = styled.span`
	font-weight: 600;
`;
const IssuesGrid = styled.div`
	display: flex;
	gap: 16px;
	width: 100%;
	margin-bottom: 40px;

	${media.lessThan("medium")`
		flex-direction: column;
		margin-bottom: 32px;
	`}
`;
const IssueItem = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	align-items: flex-start;
	gap: 8px;
	min-width: 0;

	${media.lessThan("medium")`
		flex-direction: row;
		align-items: flex-start;
		gap: 12px;
	`}
`;
const IssueIconWrap = styled.div`
	display: flex;
	width: 40px;
	height: 40px;
	align-items: center;
	justify-content: center;
	border-radius: 999px;
	background: var(--surface-background, #f9f6f3);
	flex-shrink: 0;

	${media.lessThan("medium")`
		background: white;
	`}
`;
const IssueText = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
`;
const IssueLabel = styled(Text)`
	font-weight: 500;
	line-height: 24px;
`;
const IssueDesc = styled(Text)`
	font-size: 13px;
	line-height: 18px;
	color: var(--text-clear-neutral-medium, rgba(0, 29, 61, 0.56));
`;
const IssueHighlight = styled.span`
	font-weight: 500;
	color: var(--text-clear-neutral-hard, rgba(0, 29, 61, 0.92));
	background: var(--surface-secondary-low, #fff1d6);
	border-radius: 4px;
	padding: 0 2px;
	box-decoration-break: clone;
	-webkit-box-decoration-break: clone;
`;
const IssueSup = styled.sup`
	font-size: 0.65em;
	vertical-align: super;
	line-height: 0;
`;
const IssuesFootnote = styled(Text)`
	color: ${ColorV2.text.neutralMedium};
`;
const IndependenceTitle = styled(Text)`
	margin-bottom: 64px;
	${media.lessThan("medium")`
		margin-bottom: 24px;
		color: var(--text-invert, #FFF);
	`}
`;
const IndependenceBold = styled.span`
	font-weight: 600;
`;
const IndependenceRow = styled.div`
	display: flex;
	flex-direction: row;
	gap: 48px;
	${media.lessThan("medium")`
		flex-direction: column;
	`}
`;
const IndependenceParagraph = styled(Text)`
	margin-bottom: 12px;
	font-size: 15px;
	line-height: 24px;
	${media.lessThan("medium")`
		color: var(--text-invert, #FFF);
	`}
`;
const IndependenceParagraphLast = styled(IndependenceParagraph)`
	margin-bottom: 0;
`;
const MercedesCaption = styled(Text)`
	color: ${ColorV2.text.neutralMedium};
	margin-top: 4px;
	text-align: right;
	${media.lessThan("medium")`
		color: var(--text-clear-invert-high, rgba(255, 255, 255, 0.68));
	`}
`;
const ReviewsRow = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	flex-wrap: wrap;
	gap: 12px;
	margin-bottom: 32px;
	${media.lessThan("medium")`
		margin-bottom: 24px;
	`}
`;
const PuntuationBadge = styled.div`
	display: inline-flex;
	flex-direction: row;
	align-items: center;
	gap: 6px;
	padding: 8px 16px;
	border: 1px solid ${ColorV2.border.neutralSoft};
	border-radius: 1000px;
`;
const StarsRow = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 2px;
`;
const ImpactSection = styled.div`
	display: flex;
	flex-direction: column;
	padding: 64px 0;
	border-bottom: 1px solid ${ColorV2.border.neutralSoft};

	${media.lessThan("medium")`
		background: var(--surface-background, #F9F6F3);
		box-sizing: border-box;
		width: 100vw;
		margin-left: calc(50% - 50vw);
		margin-right: calc(50% - 50vw);
		padding: 48px 24px;
		border-bottom: none;
	`}
`;
const ImpactContainer = styled.div`
	display: flex;
	gap: 16px;
	${media.lessThan("medium")`
		flex-direction: column;
	`}
`;
const ImpactStats = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 0px 32px;
	flex: 1;
	${media.lessThan("medium")`
		gap: 32px 16px;
	`}
`;
const ImpactCard = styled.div`
	display: flex;
	gap: 8px;
	width: calc(50% - 16px);
	${media.lessThan("medium")`
		width: calc(50% - 8px);
	`}
`;
const ImpactIconWrap = styled.div`
	width: 24px;
	padding-top: 4px;
	flex-shrink: 0;
`;
const ImpactStatText = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
	flex: 1;
`;
const ImpactStatNumber = styled(Text)`
	font-size: 28px !important;
	line-height: 36px !important;
	font-weight: 500 !important;
	margin: 0;
`;
const ImpactStatLabel = styled(Text)`
	font-size: 15px !important;
	line-height: 24px !important;
	margin: 0;
`;
const ImpactImageWrap = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 8px;
	min-width: 0;
	${media.lessThan("medium")`
		margin-top: 24px;
	`}
`;
const ImpactImage = styled.img`
	width: 100%;
	height: 300px;
	object-fit: cover;
	border-radius: 16px;
	${media.lessThan("medium")`
		height: 240px;
	`}
`;
const ImpactImageCaption = styled(Text)`
	font-size: 13px !important;
	line-height: 18px !important;
	color: ${ColorV2.text.neutralMedium};
	text-align: right;
	margin: 0;
`;
const DataImage = styled.img`
	display: flex;
	flex: 1;
	object-fit: cover;
	width: 100%;
	height: 100%;
	border-radius: 24px;
`;

const Info = (props: Props) => {
	const [showRelief, setShowRelief] = useState(false);
	const [, setPriceAmount] = useState("0");

	useEffect(() => {
		getReliefAmount(props.price);
	}, [props.price]);

	const getReliefAmount = (price: number) => {
		const reliefSection1 = price > 250 ? 250 : price;
		const reliefSection2 = price > 250 ? price - 250 : 0;
		const relief = reliefSection1 * 0.8 + reliefSection2 * 0.4;
		setPriceAmount(relief.toFixed(2));
	};

	const chartOptions = [
		{
			title: "Actividades con personas mayores",
			value: 90,
			color: ColorV2.text.primary,
			textColor: ColorV2.text.primary,
			fontSize: 120,
			bullets: [
				"Los mayores no pagan nada por participar",
				"Acompañamiento semanal",
				"Celebramos su cumpleaños",
				"Asistencia a nuestras actividades",
				"Cumplimos sus sueños",
				"Asistencia psicológica en casos de depresión",
			],
		},
		{
			title: "Costes operacionales",
			value: 8,
			color: ColorV2.text.neutralHard,
			textColor: ColorV2.text.neutralHard,
			fontSize: 48,
			bullets: [
				"Nos permiten funcionar como organización (alquiler de oficina y suministros)",
				"Impuestos",
				"Inversión en tecnología para llegar a todos los mayores del mundo",
			],
		},
		{
			title: "Comisiones bancarias",
			value: 2,
			color: ColorV2.text.neutralMedium,
			textColor: ColorV2.text.neutralMedium,
			fontSize: 32,
		},
	];

	const activities = [
		{
			title: "El sueño de Juan",
			description:
				"Cumplimos el sueño de mayores como Juan, que siempre había querido conocer las Islas Canarias. Se enamoró de Maspalomas, probó sus platos típicos y ¡vio el Océano Atlántico por primera vez a sus 100 años!",
			quote:
				"Todo de Canarias me gusta mucho. Esto para mí es lo máximo",
			img: require("../../../assets/images/history/juan.webp"),
			video: "https://youtube.com/shorts/vEN0vAMK_6k",
		},
		{
			title: "Matilde y Luis en un taller de Abuelos Hackers",
			description:
				"Todos los meses realizamos actividades en residencias como Abuelos Hackers, en el que enseñamos tecnología a a las personas mayores.",
			quote:
				"¡Que vuelvan todos a visitarnos! Nos lo pasamos pipa con vosotros.",
			img: require("../../../assets/images/history/foto_voluntario.webp"),
			video: "https://youtube.com/shorts/W_V6z_Ilm78",
		},
		{
			title: "Los 93 años de Loli",
			description:
				"Organizamos sus fiestas de cumpleaños haciendo que su día sea inolvidable ¡Tarta, velas, globos, regalos y muchos invitados!",
			quote: "Les daría un abrazo a todos, porque sois maravillosos.",
			img: require("../../../assets/images/history/loli.webp"),
			video: "https://www.youtube.com/watch?v=9BoL3KmF97M&list=PL2bO3y1ZkJ312Ovt0hS_v3G6oZsPwQkkA",
		},
		{
			title: "Raquel con su voluntario Eric",
			description:
				"Con nuestro programa original de voluntariado, conectamos a jóvenes con personas mayores para que les visiten todas las semanas. Una experiencia enriquecedora para mayores y voluntarios.",
			quote: "Gracias al programa de Adopta Un Abuelo ya tengo una amistad más.",
			img: require("../../../assets/images/history/raquel.webp"),
			video: "https://youtube.com/shorts/4jzq8rOR8l0",
		},
		{
			title: "Felisa recibe una felicitación de Navidad",
			description:
				"Todas las navidades llenamos las residencias de felicitaciones y conseguimos que miles de mayores se sientan acompañados.",
			quote: "Me parece muy bonito porque, sin conocernos, da la sensación de que están a nuestro lado y que nos conocen.",
			img: require("../../../assets/images/history/felisa.webp"),
			video: "https://www.youtube.com/watch?v=D--PsMjx6Ys",
		}

	];

	const testimonies = [
		{
			name: "Sara Medina Expósito",
			image: require("../../../assets/images/testimonies/sara_medina.webp"),
			date: "Hace un mes",
			testimony:
				"Hoy hemos tenido un voluntariado con Lidia la chica de redes y ha sido maravilloso. Hemos ido a una residencia a pintar con personas mayores y es una experiencia increíble, como se sienten ellos y como nos sentimos nosotros por estar con ellos. Encantada de poder participar en estas iniciativas y que sigan realizándose. Lidia una chica súper simpática que ha hecho muy dinámica la actividad.",
		},
		{
			name: "Irene Martín",
			image: require("../../../assets/images/testimonies/irene_martin.webp"),
			date: "Hace 3 meses",
			testimony:
				"Me ha encantado la experiencia. Compartir momentos con gente mayor súper interesante es de lo mejor que podemos hacer para las dos generaciones. Deseando repetir.",
		},
		{
			name: "Katherin Ambrosi",
			image: require("../../../assets/images/testimonies/katherin_ambrosi.webp"),
			date: "Hace 3 meses",
			testimony:
				"Me encantó la actividad de compartir una tarde de conversación e historias. Pasé un momento muy bonito y cercano. Disfruté especialmente hablando con María del Carmen; fue una charla muy agradable y llena de muchas historias. Una experiencia muy especial!",
		},
		{
			name: "Alba Suñé",
			image: require("../../../assets/images/testimonies/alba_suñe.webp"),
			date: "Hace 3 meses",
			testimony:
				"Una experiencia increíble y muy humana. Participé en el evento organizado por Adopta a un Abuelo y solo puedo decir cosas buenas. La fundación crea espacios muy respetuosos, emotivos y auténticos donde personas mayores que a menudo sufren soledad reciben atención, escucha y compañía real. Vi cómo los voluntarios conectaban de verdad con los mayores, compartiendo historias, risas y momentos que difícilmente se olvidan. La labor de la organización va mucho más allá de una simple actividad puntual: fomenta vínculos intergeneracionales que enriquecen tanto a los mayores como a los voluntarios, ayudando a combatir la soledad no deseada y a hacer sentir a estas personas valoradas y queridas. Además, el equipo fue profesional y acogedor, cuidando cada detalle del evento y haciendo que todos nos sintiéramos bienvenidos. Recomiendo esta ONG a quien quiera vivir una experiencia humana transformadora y ayudar de verdad a quienes más lo necesitan.",
		},
		{
			name: "Alicia Miró",
			image: require("../../../assets/images/testimonies/alicia_miro.webp"),
			date: "Hace 3 meses",
			testimony:
				"Me encanta la labor de la fundación. He colaborado ya en varias iniciativas, como enviar cartas por Navidad, visitar a una persona mayor recurrentemente, y actividades en residencias, como un bingo musical y taller de pintura, y la experiencia ha sido siempre muy gratificante. Organizan las cosas bien y sus iniciativas son preciosas.",
		},
		{
			name: "Alejandra Acevedo",
			image: require("../../../assets/images/testimonies/alejandra_acevedo.webp"),
			date: "Hace 6 meses",
			testimony:
				"Ha sido una experiencia muy bonita, agradable, sentimental y especial. Rosa es una persona agradable, con mucho amor que dar y nos ha encantado escuchar sus historias y compartir tiempo junto a ella.❤️❤️",
		},
		{
			name: "Andrea Guedez",
			image: require("../../../assets/images/testimonies/andrea_guedez.webp"),
			date: "Hace 7 meses",
			testimony:
				"Fue uno de esos días que me dejan el corazón lleno 🩵 Estuve en una actividad presencial con Adopta un Abuelo, una organización de la que tengo la bendición de ser voluntaria desde hace dos años y que hace un trabajo increíble por los mayores de nuestra sociedad, dándoles el cariño y la compañía que tanto merecen.",
		},
		{
			name: "Vanessa Fernandez",
			image: require("../../../assets/images/testimonies/natalia_lopez.webp"),
			date: "Hace 7 meses",
			testimony:
				"Muy contenta de haber pasado una mañana con Ramón y los demás mayores. Son graciosos, inteligentes, tienen muchísimo conocimiento musical y hasta bailan. Lo recomiendo mucho y no dudo que volveré muy pronto.",
		},
		{
			name: "Natalia López",
			image: require("../../../assets/images/testimonies/natalia_lopez_2.webp"),
			date: "Hace 7 meses",
			testimony:
				"Qué rato tan bueno hemos pasado con la influencer Ana. Gran cocinera, llena de vida y con muchas ganas de aprender y pasarlo bien. Cuándo repetimos???",
		},
		{
			name: "Jimena González",
			image: require("../../../assets/images/testimonies/jimena_gonzalez.webp"),
			date: "Hace 8 meses",
			testimony:
				"Venir a celebrar cumpleaños a Adopta un Abuelo es una de las experiencias más entrañables que he vivido nunca. La cara de emoción de los abuelos es indescriptible, y la relación que se crea entre los voluntarios (entre los que me incluyo con mucho orgullo), es muy especial. ¡Y además tienen tarta gratis!",
		},
	];

	const press: Array<{
		text: string;
		logo:
		| "a3"
		| "abc"
		| "cope"
		| "cuatro"
		| "forbes"
		| "mundo"
		| "onda"
		| "pais"
		| "rtve"
		| "telecinco"
		| "tve"
		| "rne"
		| "20minutos"
		| "sexta"
		| "ser";
	}> = [
			{
				text: 'Los jóvenes reivindican el papel de los abuelos: "Las personas mayores cuentan mejores historias que Netflix".',
				logo: "rtve",
			},
			{
				text: 'Jóvenes y mayores se unen para luchar contra la soledad: "Me da lecciones de vida".',
				logo: "a3",
			},
			{
				text: "Una ONG manchega pone en contacto a jóvenes y mayores necesitados de compañía.",
				logo: "pais",
			},
			{
				text: "Adopta un abuelo: la iniciativa para conectar a generaciones y combatir la soledad.",
				logo: "onda",
			},
			{
				text: "Juan cumple el sueño de su vida de conocer Canarias a los 100 años.",
				logo: "abc",
			},
			{
				text: "Una carta para un abuelo: La emocionante iniciativa navideña para combatir la soledad no deseada en España.",
				logo: "cope",
			},
			{
				text: 'La lección de vida de Pilar, que ha cumplido su sueño a los 84 años: "Mi nieto me dijo que no se me ocurriese hacerlo".',
				logo: "telecinco",
			},
			{
				text: "La magia de un cumpleaños compartido: conecta generaciones y reduce la soledad en las personas mayores.",
				logo: "rne",
			},
			{
				text: "Reinventando el ‘match’ para acabar con la soledad no deseada en la vejez.",
				logo: "20minutos",
			},
			{
				text: "La emoción de Ángeles, una anciana madrileña, al regresar al Retiro y revivir los momentos más especiales de su vida.",
				logo: "sexta",
			},
			{
				text: "Adopta un abuelo, la iniciativa que nació en Ciudad Real y que se ha convertido en un fenómeno social a nivel mundial.",
				logo: "ser",
			},
			{
				text: "'Adopta un abuelo', la iniciativa que calma la soledad de los ancianos.",
				logo: "mundo",
			},
		];

	const data = [
		{
			icon: RockingChair,
			value: "+15.000",
			label: "abuelos adoptados",
		},
		{
			icon: Users,
			value: "+26.000",
			label: "voluntarios",
		},
		{
			icon: Clock,
			value: "+91.000",
			label: "horas de compañía",
		},
		{
			icon: CalendarCheck,
			value: "+600",
			label: "actividades realizadas",
		},
	];

	return (
		<Container style={props.style}>
			<ReliefModal
				isVisible={showRelief}
				price={15}
				charge="monthly"
				onClose={() => setShowRelief(false)}
			/>

			<Section>
				<IntroRow>
					<IntroMain>
						<IntroTitle type="h3" weight="semibold">
							Los grandes proyectos se crean en comunidad
						</IntroTitle>
						<IntroLead type="h6">
							Siendo embajador muestras tu compromiso con las personas mayores a través de nuestra fundación. Con una donación mensual o anual haces posible que acompañemos a personas mayores todos los días.
						</IntroLead>
					</IntroMain>

					<ReliefCard>
						<ReliefIcon
							alt="Moneda sobre mano"
							src={require("../../../assets/images/icon/coinLowerHand.webp")}
						/>
						<ReliefText>
							<ReliefBody type="h6">
								Además, <ReliefBold>te devuelven el 80%</ReliefBold> de tu donación en la Declaración de la Renta.
							</ReliefBody>
						</ReliefText>
					</ReliefCard>
				</IntroRow>

				<EmbajadorImage
					alt="Comunidad de Adopta Un Abuelo en los Premios 2025"
					src={require("../../../assets/images/donation/premios-comunidad.webp")}
				/>
			</Section>

			<IssuesSectionOuter>
				<IssuesSection>
					<IssuesTitle type="h3">
						<IssuesLead>
							Por qué tu donación es importante.
						</IssuesLead>{" "}
						Conoce los problemas sociales a los que se enfrentan las
						personas mayores.
					</IssuesTitle>

					<IssuesGrid>
						<IssueItem>
							<IssueIconWrap>
								<HeartCrack
									color={ColorV2.text.neutralHard}
									height={20}
									width={20}
								/>
							</IssueIconWrap>
							<IssueText>
								<IssueLabel type="p">Soledad no deseada</IssueLabel>
								<IssueDesc type="c2">
									Se estima que en España +2.000.000 de personas mayores viven solas.<IssueSup>1</IssueSup>{" "}
									<IssueHighlight>
										La soledad equivale a fumar 15 cigarrillos al día.<IssueSup>2</IssueSup>
									</IssueHighlight>
								</IssueDesc>
							</IssueText>
						</IssueItem>

						<IssueItem>
							<IssueIconWrap>
								<Smartphone
									color={ColorV2.text.neutralHard}
									height={20}
									width={20}
								/>
							</IssueIconWrap>
							<IssueText>
								<IssueLabel type="p">Brecha digital</IssueLabel>
								<IssueDesc type="c2">
									Más del 65% de las personas mayores no sabe hacer gestiones digitales y están excluidas.<IssueSup>3</IssueSup>
								</IssueDesc>
							</IssueText>
						</IssueItem>

						<IssueItem>
							<IssueIconWrap>
								<Glasses
									color={ColorV2.text.neutralHard}
									height={20}
									width={20}
								/>
							</IssueIconWrap>
							<IssueText>
								<IssueLabel type="p">Edadismo</IssueLabel>
								<IssueDesc type="c2">
									Las personas mayores sufren discriminación y prejuicios por razón de su edad.
								</IssueDesc>
							</IssueText>
						</IssueItem>
					</IssuesGrid>

					<IssuesFootnote type="c2">
						1. INE, Encuesta Continua de Hogares | 2. El País, La factura económica de la soledad | 3. Fundación Ferrer i Guàrdia, 2024/2025.
					</IssuesFootnote>
				</IssuesSection>
			</IssuesSectionOuter>

			<CarouselSection>
				<SectionTitleH2 type="h3" weight="semibold">
					Conoce algunas de las historias que hacemos posibles gracias a tu donación
				</SectionTitleH2>
				<ActivitiesList activities={activities} />
			</CarouselSection>

			<DestinationSection>
				<IssuesTitle type="h3">
					<IssuesLead>
						Somos transaparentes:
					</IssuesLead>{" "}
					te mostramos a dónde se destina tu donación
				</IssuesTitle>
				<ProtectedDonationWrap>
					<ProtectedDonation type="donation" />
				</ProtectedDonationWrap>
				<ProgressBar>
					<ProgressBarSegment
						$flex={chartOptions[0].value}
						$color={ColorV2.surface.primary}
					/>
					<ProgressBarSegment
						$flex={chartOptions[1].value}
						$color={ColorV2.text.neutralHard}
					/>
					<ProgressBarSegment
						$flex={chartOptions[2].value}
						$color={ColorV2.text.neutralMedium}
					/>
				</ProgressBar>
				<ChartRow>
					<ChartColPrimary>
						<ChartHeader>
							<PercentLarge type="p">
								{chartOptions[0].value}%
							</PercentLarge>
							<ChartLabel type="p" $color={ColorV2.text.primary}>
								{chartOptions[0].title}
							</ChartLabel>
						</ChartHeader>
						<BulletsList>
							{chartOptions[0].bullets?.map((b, i) => (
								<BulletItem key={`primary-bullet-${i}`}>
									<BulletIconWrap>
										<Check
											height={18}
											width={18}
											color={ColorV2.text.primary}
										/>
									</BulletIconWrap>
									<BulletItemText type="p">{b}</BulletItemText>
								</BulletItem>
							))}
						</BulletsList>
					</ChartColPrimary>
					<ChartColNeutral>
						<ChartHeader>
							<PercentMedium type="p">
								{chartOptions[1].value}%
							</PercentMedium>
							<ChartLabel
								type="p"
								$color={ColorV2.text.neutralHard}
							>
								{chartOptions[1].title}
							</ChartLabel>
						</ChartHeader>
						<BulletsList>
							{chartOptions[1].bullets?.map((b, i) => (
								<BulletItem key={`neutral-bullet-${i}`}>
									<BulletIconWrap>
										<Check
											height={14}
											width={14}
											color={ColorV2.text.neutralHard}
										/>
									</BulletIconWrap>
									<BulletItemText type="p">{b}</BulletItemText>
								</BulletItem>
							))}
						</BulletsList>
					</ChartColNeutral>
					<ChartColMedium>
						<ChartHeader>
							<PercentSmall type="p">
								{chartOptions[2].value}%
							</PercentSmall>
							<ChartLabel
								type="p"
								$color={ColorV2.text.neutralMedium}
							>
								{chartOptions[2].title}
							</ChartLabel>
						</ChartHeader>
					</ChartColMedium>
				</ChartRow>
			</DestinationSection>

			<IndependenceSection>
				<IndependenceTitle type="h3">
					<IndependenceBold>Somos una fundación independiente.</IndependenceBold> Nos sentimos orgullosos de no recibir subvenciones
				</IndependenceTitle>
				<IndependenceRow>
					<Col>
						<IndependenceParagraph type="p">
							Nuestra actividad se financia gracias al apoyo de nuestros embajadores.
						</IndependenceParagraph>
						<IndependenceParagraph type="p">
							No aceptamos subvenciones públicas para garantizar nuestra independencia con el objetivo de defender los derechos de las personas mayores frente a quién sea y en cualquier circunstancia.
						</IndependenceParagraph>
						<IndependenceParagraphLast type="p">
							Los mayores y sus derechos siempre primero.
						</IndependenceParagraphLast>
					</Col>
					<Col>
						<DataImage
							src={require("../../../assets/images/donation/mercedes.webp")}
						/>
						<MercedesCaption type="c1">
							Mercedes y su voluntaria Teresa
						</MercedesCaption>
					</Col>
				</IndependenceRow>
			</IndependenceSection>

			<SectionDivider>
				<SectionTitleH2Compact type="h3" weight="semibold">
					Más de 2.000 personas reconocen nuestra labor social
				</SectionTitleH2Compact>
				<ReviewsRow>
					<PuntuationBadge>
						<Google size={20} />
						<Text type="h5" weight="semibold">
							4,9
						</Text>
						<StarsRow>
							{Array.from({ length: 5 }).map((_, index) => (
								<Star
									key={`review-star-${index}`}
									size={18}
									fill={ColorV2.surface.secondary}
									color={ColorV2.surface.secondary}
								/>
							))}
						</StarsRow>
					</PuntuationBadge>
				</ReviewsRow>
				<TestimonyList options={testimonies} />
			</SectionDivider>

			<ImpactSection>
				<SectionTitleH2 type="h2" weight="semibold">
					Nuestro impacto social en cifras
				</SectionTitleH2>
				<ImpactContainer>
					<ImpactStats>
						{data.map((item, index) => {
							const Icon = item.icon;
							return (
								<ImpactCard key={`impact-${index}`}>
									<ImpactIconWrap>
										<Icon
											height={24}
											width={24}
											color={ColorV2.text.primary}
										/>
									</ImpactIconWrap>
									<ImpactStatText>
										<ImpactStatNumber type="p">
											{item.value}
										</ImpactStatNumber>
										<ImpactStatLabel type="p">
											{item.label}
										</ImpactStatLabel>
									</ImpactStatText>
								</ImpactCard>
							);
						})}
					</ImpactStats>
					<ImpactImageWrap>
						<ImpactImage
							alt="María Antonia en uno de nuestros talleres"
							src={require("../../../assets/images/donation/maria-antonia.webp")}
						/>
						<ImpactImageCaption type="c1">
							María Antonia en uno de nuestros talleres
						</ImpactImageCaption>
					</ImpactImageWrap>
				</ImpactContainer>
			</ImpactSection>

			<SectionDivider>
				<IssuesTitle type="h3">
					<IssuesLead>
						Hablan de nosotros:
					</IssuesLead>{" "}
					nos han mencionado más de 1.700 veces en los principales medios
				</IssuesTitle>
				<PressList options={press} />
			</SectionDivider>

			<Section id="faqs">
				<Text type="h3" weight="semibold" style={{ marginBottom: '24px' }}>
					No te quedes con ninguna duda
				</Text>
				<FAQs
					options={[
						{
							id: "item-1",
							title: "¿Es fiscalmente deducible?",
							description:
								"Sí. Al tratarse de una donación a una fundación, podrás desgravarla en tu declaración de la renta. Te aparecerá directamente en el borrador.",
						},
						{
							id: "item-2",
							title: "¿A qué se destina la donación?",
							description:
								"El 90% de las donaciones se destinan a las actividades con personas mayores: visitas semanales, celebraciones de cumpleaños, actividades intergeneracionales y experiencias especiales. Invertimos un 8% en costes operacionales y un 2% en comisiones bancarias.",
						},
						{
							id: "item-3",
							title: "¿Cuál es el importe de la donación?",
							description:
								"Tú decides con cuánto quieres colaborar. Puedes elegir entre tres importes de donación, cada uno asociado a un número de horas de compañía. Elige la opción que mejor se adapte a ti.",
						},
						{
							id: "item-4",
							title: "¿Hay otra forma de ayudar que no sea con dinero?",
							description:
								"Sí. Puedes ayudarnos dando difusión al programa entre familiares y amigos o participando como voluntario a través de nuestra app.",
						},
						{
							id: "item-5",
							title: "¿Qué implica ser embajador?",
							description:
								"Ser embajador no implica ningún compromiso de permanencia. Tu aportación nos permite seguir acompañando a personas mayores y puedes modificarla o cancelarla cuando quieras.",
						},
					]}
				/>
			</Section>
		</Container>
	);
};
export default Info;
export interface Props {
	style?: CSSProperties;
	price: number;
	children?: React.ReactNode;
	onClick?: () => void;
}
